/**
 * IS THE SHIFT LIST THE SCREEN SHOWING?
 *
 * The registration step has two screens for a volunteer: the ways in, then
 * the shifts. Which one is showing used to be answered by a remembered flag
 * alone, and a flag outlives the thing it was about. Send Sarah to Volunteer,
 * close the sheet, untick her and tick yourself: the party is rebuilt as you,
 * the flag is not, and the sheet asks what YOU are helping with for a way in
 * you never picked. The footer then asks for a shift against a registration
 * with no door, which cannot be given, so there is no way forward and the
 * way-in question is unreachable.
 *
 * So the screen is a question about the party rather than a memory. The flag
 * still says the viewer stepped INTO the shifts; this says the registration
 * they stepped into is still one that takes them.
 */
export function showsShifts(
  picking: boolean,
  door: { takesShifts: boolean } | null | undefined,
  shiftCount: number
): boolean {
  return picking && Boolean(door?.takesShifts) && shiftCount > 0;
}

/**
 * WHETHER THE FOOTER OFFERS A WAY BACK.
 *
 * Entered from the page, the who step is not in this sheet at all, so the
 * first person's first step has nothing behind it and the button is hidden.
 *
 * THE SHIFTS ARE THE EXCEPTION, and they were the bug. They are the SECOND
 * screen of the type step, so the way in is always behind them however the
 * sheet was entered — but the rule above counted the whole step as the first
 * one and hid Back. A volunteer who picked the wrong way in, or anybody
 * registering only themselves, reached the shift list with no route back to
 * the question that put them there.
 */
export function offersBack(where: {
  /** The shift list is showing, so the way in is one press behind. */
  onShifts: boolean;
  atWhoStep: boolean;
  atFirstStep: boolean;
  atFirstPerson: boolean;
  /** The page already asked who is coming, so the who step is not in here. */
  seeded: boolean;
}): boolean {
  if (where.onShifts) return true;
  if (where.atWhoStep) return false;
  return !(where.seeded && where.atFirstStep && where.atFirstPerson);
}

/**
 * THE WAY IN A DRAFT HAS PICKED, OR NULL.
 *
 * The draft is optional because THE PARTY IS LEGITIMATELY EMPTY. The sheet is
 * rendered on the page the whole time, closed, and `seed()` returns [] for a
 * signed-in viewer who has ticked nobody — which is every ordinary page load
 * by somebody with an account. A version of this that assumed a draft read
 * `.doorId` off undefined during render and took the entire event page down
 * with it: the page painted, the viewer resolved, and the widget unmounted.
 *
 * So the check lives here, once, rather than at each of the six call sites
 * and reliably at five of them.
 */
export function doorFor<D extends { id: number }>(
  doors: readonly D[],
  r: { doorId?: number | null } | undefined
): D | null {
  if (!r?.doorId) return null;
  return doors.find((d) => d.id === r.doorId) ?? null;
}

/**
 * WHETHER A BAND HEADING IS WORTH DRAWING.
 *
 * Grouping by hours is the point when there are several bands: when a shift
 * runs matters more to a volunteer than what it is, so the times lead.
 *
 * But 587 of the church's 2,000 opportunities carry no times at all, and a
 * single band with nothing to say puts one "Any time" heading over the whole
 * list — a label every row shares, which is no label. One band WITH times is
 * different and stays: "10:00 AM to 2:00 PM" over seven Fall Family Fest roles
 * is the commitment being asked for, and it is the same for all of them
 * precisely because staff set it that way.
 */
export function showsBand(bandCount: number, start: string | null): boolean {
  return bandCount > 1 || Boolean(start);
}
